/**
 * MUI DataTable state'ini API query params'a dönüştürür.
 * Bracket notation filtre formatı: field[operator]=value
 */
export function parseTableParams(tableState, columns) {
  const params = {};

  // Sayfalama
  params.pageNumber = (tableState.page || 0) + 1; // 0-indexed → 1-indexed
  params.pageSize = tableState.rowsPerPage || 10;

  // Sıralama — kolonun `options.sortKey`'i backend orderBy switch anahtarıdır (ör.
  // candidateFullName → candidatename); yoksa kolon adı gider (backend küçük harfe indirir).
  if (tableState.sortOrder?.name) {
    const sortColumn = columns?.find((c) => c.name === tableState.sortOrder.name);
    params.orderBy = sortColumn?.options?.sortKey || tableState.sortOrder.name;
    params.orderDir = tableState.sortOrder.direction || 'asc';
  }

  // Filtreler
  if (tableState.filterList && columns) {
    tableState.filterList.forEach((filterValues, index) => {
      if (!filterValues || filterValues.length === 0) return;

      const column = columns[index];
      if (!column) return;

      const fieldName = column.name;

      filterValues.forEach((val) => {
        if (val === null || val === undefined || val === '') return;

        if (typeof val === 'object' && val.op && val.val !== undefined) {
          // Özel operatör: { op: 'between', val: ['2024-01-01', '2024-12-31'] }
          if (val.op === 'between' && Array.isArray(val.val)) {
            params[`${fieldName}[between]`] = val.val.join(',');
          } else {
            params[`${fieldName}[${val.op}]`] = val.val;
          }
        } else {
          // Düz değer: `filterOptions.filterKey` backend anahtarıdır ve operatörünü içinde
          // taşır (ör. candidateName[like]); yoksa name[defaultOp] (varsayılan like).
          const filterOptions = column.options?.filterOptions;
          const key = filterOptions?.filterKey || `${fieldName}[${filterOptions?.defaultOp || 'like'}]`;
          params[key] = val;
        }
      });
    });
  }

  return params;
}

/**
 * Tarih string'ini uygulama diline göre formatlar.
 * @param {string|Date} value — ISO tarih string'i veya Date
 * @param {string} locale — i18n.language (tr, en, ru, kk)
 * @param {object} options — Intl.DateTimeFormat options (varsayılan: sadece tarih)
 * @returns {string} — Yerelleştirilmiş tarih string'i veya '-'
 */
export function formatDate(value, locale = 'tr', options) {
  if (!value) return '-';
  try {
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString(locale, options || { year: 'numeric', month: '2-digit', day: '2-digit' });
  } catch {
    return '-';
  }
}

/**
 * Tarih + saat formatlar.
 */
/**
 * API yanıtından liste verisini güvenli şekilde çıkarır.
 * Hem PaginatedList ({ data: [], totalRowCount }) hem düz array formatını destekler.
 */
export function extractListData(res) {
  const result = res.data?.data;
  return Array.isArray(result) ? result : (result?.data || []);
}

export function formatDateTime(value, locale = 'tr') {
  if (!value) return '-';
  try {
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleString(locale, {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '-';
  }
}

// Normalize a datetime form value to an absolute UTC ISO-8601 string ("...Z")
// before sending to the API. Offset-less "yyyy-MM-ddTHH:mm" values (from
// datetime-local inputs / default builders) are parsed as browser-LOCAL time,
// so toISOString converts the wall time to the correct UTC instant; values that
// already carry an offset (e.g. the MUI picker's toISOString output) pass
// through unchanged. Without this, the backend stamps an offset-less value as
// UTC WITHOUT shifting (Npgsql legacy timestamp behavior), storing the instant
// hours off in any non-UTC timezone. Returns null for empty/invalid input.
export function toUtcIso(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return isNaN(date.getTime()) ? null : date.toISOString();
}

// ASCII-only e-mail format check. Mirrors the backend EmailValidation rule so the
// UI rejects addresses with non-English letters (e.g. örnek@fırma.cöm) before submit.
const ASCII_EMAIL = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
export function isValidEmail(value) {
  return typeof value === 'string' && ASCII_EMAIL.test(value.trim());
}
